import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
  Chip,
  Tooltip,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Slider,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  ButtonGroup,
} from "@mui/material";
import {
  Delete,
  Save,
  Download,
  Undo,
  Redo,
  ZoomIn,
  ZoomOut,
  ZoomOutMap,
  Straighten,
  ArrowForward,
  Circle,
  Rectangle,
  TextFields,
  Comment,
  Warning,
  Error,
  CheckCircle,
  Star,
  Place,
  Highlight,
  LayersClear,
  Layers,
  Tune,
  Create,
} from "@mui/icons-material";

const PpPhotoAnnotationTools = ({
  photo,
  photoList = [],
  onSave,
  onCancel,
  readOnly = false,
  existingAnnotations = [],
}) => {
  // Canvas and drawing state
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [ctx, setCtx] = useState(null);
  const [image, setImage] = useState(null);
  const [annotations, setAnnotations] = useState(existingAnnotations);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentTool, setCurrentTool] = useState("pen"); // pen, arrow, rectangle, circle, text, measurement
  const [drawingColor, setDrawingColor] = useState("#ff0000");
  const [lineWidth, setLineWidth] = useState(3);
  const [fillOpacity, setFillOpacity] = useState(0.3);
  const [fontSize, setFontSize] = useState(16);

  // Canvas state
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [draftAnnotation, setDraftAnnotation] = useState(null);

  // Active photo selection (allows switching between images)
  const [activePhoto, setActivePhoto] = useState(photo);
  useEffect(() => {
    setActivePhoto(photo);
  }, [photo]);

  // Annotation state
  // Selection of existing annotations can be implemented later
  const [annotationDialog, setAnnotationDialog] = useState(false);
  const [currentAnnotation, setCurrentAnnotation] = useState({
    id: "",
    type: "comment",
    x: 0,
    y: 0,
    text: "",
    color: "#ff0000",
    priority: "normal",
    author: "Current User",
    timestamp: new Date().toISOString(),
  });

  // Measurement state
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const [realWorldScale, setRealWorldScale] = useState(1); // pixels per unit
  const [measurementUnit, setMeasurementUnit] = useState("feet");
  const [calibrationMode, setCalibrationMode] = useState(false);
  const [knownDistance, setKnownDistance] = useState("");

  // UI state
  const [toolsPanelOpen, setToolsPanelOpen] = useState(true);
  const [layersPanelOpen, setLayersPanelOpen] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [gridSize, setGridSize] = useState(20);

  const annotationTypes = [
    { value: "comment", label: "Comment", icon: <Comment />, color: "#2196f3" },
    { value: "issue", label: "Issue", icon: <Warning />, color: "#ff9800" },
    { value: "error", label: "Error", icon: <Error />, color: "#f44336" },
    {
      value: "approved",
      label: "Approved",
      icon: <CheckCircle />,
      color: "#4caf50",
    },
    {
      value: "measurement",
      label: "Measurement",
      icon: <Straighten />,
      color: "#9c27b0",
    },
    {
      value: "highlight",
      label: "Highlight",
      icon: <Star />,
      color: "#ffeb3b",
    },
    { value: "location", label: "Location", icon: <Place />, color: "#795548" },
  ];

  const colorPalette = [
    "#f44336",
    "#e91e63",
    "#9c27b0",
    "#673ab7",
    "#3f51b5",
    "#2196f3",
    "#03a9f4",
    "#00bcd4",
    "#009688",
    "#4caf50",
    "#8bc34a",
    "#cddc39",
    "#ffeb3b",
    "#ffc107",
    "#ff9800",
    "#ff5722",
    "#795548",
    "#9e9e9e",
    "#607d8b",
    "#000000",
  ];

  const drawingTools = [
    { id: "pen", icon: <Create />, label: "Free Draw" },
    { id: "arrow", icon: <ArrowForward />, label: "Arrow" },
    { id: "rectangle", icon: <Rectangle />, label: "Rectangle" },
    { id: "circle", icon: <Circle />, label: "Circle" },
    { id: "text", icon: <TextFields />, label: "Text" },
    { id: "measurement", icon: <Straighten />, label: "Measurement" },
    { id: "highlight", icon: <Highlight />, label: "Highlight" },
  ];

  // Initialize canvas and image
  useEffect(() => {
    if (activePhoto && canvasRef.current) {
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      setCtx(context);

      // Load image
      const img = new Image();
      img.onload = () => {
        setImage(img);

        // Set canvas size to match container
        const container = containerRef.current;
        if (container) {
          const containerRect = container.getBoundingClientRect();
          canvas.width = containerRect.width;
          canvas.height = containerRect.height;

          // Calculate initial scale and offset to fit image
          const scaleX = canvas.width / img.width;
          const scaleY = canvas.height / img.height;
          const initialScale = Math.min(scaleX, scaleY, 1);

          setScale(initialScale);
          setOffset({
            x: (canvas.width - img.width * initialScale) / 2,
            y: (canvas.height - img.height * initialScale) / 2,
          });
        }
      };

      img.onerror = (error) => {
        console.error("Failed to load image:", error);
      };

      img.crossOrigin = "anonymous";
      const imageSrc = activePhoto.url || activePhoto.dataUrl;
      img.src = imageSrc;
    }
  }, [activePhoto]);

  const calculateDistance = useCallback(
    (point1, point2) => {
      const pixelDistance = Math.sqrt(
        Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2)
      );
      return pixelDistance / realWorldScale;
    },
    [realWorldScale]
  );

  const redrawCanvas = useCallback(() => {
    if (!ctx || !image) return;

    const canvas = canvasRef.current;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Save context
    ctx.save();

    // Apply transforms
    ctx.translate(offset.x, offset.y);
    ctx.scale(scale, scale);

    // Draw image
    ctx.drawImage(image, 0, 0);

    // Draw grid if enabled
    if (showGrid) {
      // Draw grid inline to avoid external dependency
      ctx.save();
      ctx.strokeStyle = "rgba(255, 255, 255, 0.3)";
      ctx.lineWidth = 1 / scale;
      const gridSpacing = gridSize;
      const imageWidth = image.width;
      const imageHeight = image.height;
      for (let x = 0; x <= imageWidth; x += gridSpacing) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, imageHeight);
        ctx.stroke();
      }
      for (let y = 0; y <= imageHeight; y += gridSpacing) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(imageWidth, y);
        ctx.stroke();
      }
      ctx.restore();
    }

    // Draw annotations
    // Draw annotations inline to avoid external dependency
    const drawAnn = (annotation) => {
      ctx.save();
      ctx.strokeStyle = annotation.color || drawingColor;
      ctx.fillStyle = annotation.color || drawingColor;
      ctx.lineWidth = annotation.lineWidth || lineWidth;
      switch (annotation.type) {
        case "pen":
          if (annotation.points && annotation.points.length > 1) {
            ctx.beginPath();
            ctx.moveTo(annotation.points[0].x, annotation.points[0].y);
            annotation.points.forEach((point) => {
              ctx.lineTo(point.x, point.y);
            });
            ctx.stroke();
          }
          break;
        case "arrow":
          if (annotation.start && annotation.end) {
            const headLength = 15;
            const angle = Math.atan2(
              annotation.end.y - annotation.start.y,
              annotation.end.x - annotation.start.x
            );
            ctx.beginPath();
            ctx.moveTo(annotation.start.x, annotation.start.y);
            ctx.lineTo(annotation.end.x, annotation.end.y);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(annotation.end.x, annotation.end.y);
            ctx.lineTo(
              annotation.end.x - headLength * Math.cos(angle - Math.PI / 6),
              annotation.end.y - headLength * Math.sin(angle - Math.PI / 6)
            );
            ctx.moveTo(annotation.end.x, annotation.end.y);
            ctx.lineTo(
              annotation.end.x - headLength * Math.cos(angle + Math.PI / 6),
              annotation.end.y - headLength * Math.sin(angle + Math.PI / 6)
            );
            ctx.stroke();
          }
          break;
        case "rectangle":
          if (annotation.start && annotation.end) {
            const width = annotation.end.x - annotation.start.x;
            const height = annotation.end.y - annotation.start.y;
            ctx.strokeRect(
              annotation.start.x,
              annotation.start.y,
              width,
              height
            );
            if (annotation.fill) {
              ctx.globalAlpha = fillOpacity;
              ctx.fillRect(
                annotation.start.x,
                annotation.start.y,
                width,
                height
              );
              ctx.globalAlpha = 1;
            }
          }
          break;
        case "circle":
          if (annotation.center && annotation.radius) {
            ctx.beginPath();
            ctx.arc(
              annotation.center.x,
              annotation.center.y,
              annotation.radius,
              0,
              2 * Math.PI
            );
            ctx.stroke();
            if (annotation.fill) {
              ctx.globalAlpha = fillOpacity;
              ctx.fill();
              ctx.globalAlpha = 1;
            }
          }
          break;
        case "text":
          if (
            annotation.x !== undefined &&
            annotation.y !== undefined &&
            annotation.text
          ) {
            ctx.font = `${annotation.fontSize || fontSize}px Arial`;
            ctx.fillText(annotation.text, annotation.x, annotation.y);
          }
          break;
        case "measurement":
          if (annotation.points && annotation.points.length >= 2) {
            ctx.save();
            ctx.strokeStyle = "#ff00ff";
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(annotation.points[0].x, annotation.points[0].y);
            ctx.lineTo(annotation.points[1].x, annotation.points[1].y);
            ctx.stroke();
            const markerSize = 5;
            annotation.points.forEach((point) => {
              ctx.beginPath();
              ctx.arc(point.x, point.y, markerSize, 0, 2 * Math.PI);
              ctx.fill();
            });
            if (annotation.distance) {
              const midX =
                (annotation.points[0].x + annotation.points[1].x) / 2;
              const midY =
                (annotation.points[0].y + annotation.points[1].y) / 2;
              ctx.font = "14px Arial";
              ctx.fillStyle = "#000000";
              ctx.strokeStyle = "#ffffff";
              ctx.lineWidth = 3;
              const text = `${annotation.distance.toFixed(2)} ${measurementUnit}`;
              ctx.strokeText(text, midX, midY - 10);
              ctx.fillText(text, midX, midY - 10);
            }
            ctx.restore();
          }
          break;
        case "highlight":
          if (annotation.area) {
            ctx.globalAlpha = 0.3;
            ctx.fillStyle = annotation.color || "#ffff00";
            ctx.fillRect(
              annotation.area.x,
              annotation.area.y,
              annotation.area.width,
              annotation.area.height
            );
            ctx.globalAlpha = 1;
          }
          break;
        default:
          break;
      }
      if (annotation.x !== undefined && annotation.y !== undefined) {
        ctx.save();
        const markerSize = 20;
        ctx.fillStyle = annotation.color || "#2196f3";
        ctx.beginPath();
        ctx.arc(annotation.x, annotation.y, markerSize / 2, 0, 2 * Math.PI);
        ctx.fill();
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();
      }
      ctx.restore();
    };

    annotations.forEach((annotation) => {
      drawAnn(annotation);
    });

    // Draw measurement points if in measurement mode
    if (currentTool === "measurement" && measurementPoints.length > 0) {
      // Inline measurement preview
      measurementPoints.forEach((point, index) => {
        ctx.save();
        ctx.fillStyle = "#ff00ff";
        ctx.beginPath();
        ctx.arc(point.x, point.y, 3, 0, 2 * Math.PI);
        ctx.fill();
        ctx.restore();
        if (index > 0) {
          const prev = measurementPoints[index - 1];
          const distance = calculateDistance(prev, point);
          ctx.save();
          ctx.strokeStyle = "#ff00ff";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(prev.x, prev.y);
          ctx.lineTo(point.x, point.y);
          ctx.stroke();
          const midX = (prev.x + point.x) / 2;
          const midY = (prev.y + point.y) / 2;
          ctx.font = "14px Arial";
          ctx.fillStyle = "#000000";
          ctx.strokeStyle = "#ffffff";
          ctx.lineWidth = 3;
          const text = `${distance.toFixed(2)} ${measurementUnit}`;
          ctx.strokeText(text, midX, midY - 10);
          ctx.fillText(text, midX, midY - 10);
          ctx.restore();
        }
      });
    }

    // Draw draft annotation preview if any
    if (draftAnnotation) {
      drawAnn(draftAnnotation);
    }

    // Restore context
    ctx.restore();
  }, [
    ctx,
    image,
    scale,
    offset,
    annotations,
    showGrid,
    currentTool,
    measurementPoints,
    draftAnnotation,
    gridSize,
    drawingColor,
    lineWidth,
    fillOpacity,
    fontSize,
    measurementUnit,
    calculateDistance,
  ]);

  // Redraw canvas when state changes
  useEffect(() => {
    if (ctx && image) {
      redrawCanvas();
    }
  }, [ctx, image, redrawCanvas]);

  // Legacy helpers removed (drawing is handled inline within redrawCanvas)

  const getCanvasCoordinates = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (event.clientX - rect.left - offset.x) / scale;
    const y = (event.clientY - rect.top - offset.y) / scale;

    if (snapToGrid) {
      return {
        x: Math.round(x / gridSize) * gridSize,
        y: Math.round(y / gridSize) * gridSize,
      };
    }

    return { x, y };
  };

  const handleMouseDown = (event) => {
    if (readOnly) return;

    const coords = getCanvasCoordinates(event);
    setIsDrawing(true);

    switch (currentTool) {
      case "measurement":
        setMeasurementPoints((prev) => {
          const next = [...prev, coords];
          // If two points selected, create measurement annotation immediately
          if (next.length === 2) {
            const distance = calculateDistance(next[0], next[1]);
            const measurementAnn = {
              id: `annotation_${Date.now()}`,
              type: "measurement",
              color: "#ff00ff",
              points: next,
              distance,
              timestamp: new Date().toISOString(),
            };
            setAnnotations((prevA) => [...prevA, measurementAnn]);
            if (calibrationMode && knownDistance) {
              const numericKnown = parseFloat(knownDistance);
              if (!isNaN(numericKnown) && numericKnown > 0) {
                const pixelDist = Math.sqrt(
                  Math.pow(next[1].x - next[0].x, 2) +
                    Math.pow(next[1].y - next[0].y, 2)
                );
                setRealWorldScale(pixelDist / numericKnown);
              }
            }
            return [];
          }
          return next;
        });
        break;

      case "pen":
        const newPenAnnotation = {
          id: `annotation_${Date.now()}`,
          type: "pen",
          color: drawingColor,
          lineWidth: lineWidth,
          points: [coords],
          timestamp: new Date().toISOString(),
        };
        setAnnotations((prev) => [...prev, newPenAnnotation]);
        break;

      case "text":
        setCurrentAnnotation({
          ...currentAnnotation,
          id: `annotation_${Date.now()}`,
          type: "text",
          x: coords.x,
          y: coords.y,
          color: drawingColor,
          fontSize: fontSize,
        });
        setAnnotationDialog(true);
        break;

      case "rectangle":
        setDraftAnnotation({
          id: `annotation_${Date.now()}`,
          type: "rectangle",
          color: drawingColor,
          lineWidth: lineWidth,
          start: coords,
          end: coords,
        });
        break;

      case "circle":
        setDraftAnnotation({
          id: `annotation_${Date.now()}`,
          type: "circle",
          color: drawingColor,
          lineWidth: lineWidth,
          center: coords,
          radius: 0,
        });
        break;

      case "arrow":
        setDraftAnnotation({
          id: `annotation_${Date.now()}`,
          type: "arrow",
          color: drawingColor,
          lineWidth: lineWidth,
          start: coords,
          end: coords,
        });
        break;

      case "highlight":
        setDraftAnnotation({
          id: `annotation_${Date.now()}`,
          type: "highlight",
          color: drawingColor,
          area: { x: coords.x, y: coords.y, width: 0, height: 0 },
        });
        break;
      default:
        break;
    }

    if (event.ctrlKey || event.metaKey) {
      setIsDragging(true);
      setDragStart({
        x: event.clientX - offset.x,
        y: event.clientY - offset.y,
      });
    }
  };

  const handleMouseMove = (event) => {
    if (!isDrawing && !isDragging) return;

    const coords = getCanvasCoordinates(event);

    if (isDragging) {
      setOffset({
        x: event.clientX - dragStart.x,
        y: event.clientY - dragStart.y,
      });
      return;
    }

    if (currentTool === "pen") {
      setAnnotations((prev) => {
        const lastAnnotation = prev[prev.length - 1];
        if (lastAnnotation && lastAnnotation.type === "pen") {
          return [
            ...prev.slice(0, -1),
            {
              ...lastAnnotation,
              points: [...lastAnnotation.points, coords],
            },
          ];
        }
        return prev;
      });
    } else if (draftAnnotation) {
      // Update draft shape while drawing
      switch (draftAnnotation.type) {
        case "rectangle":
          setDraftAnnotation({ ...draftAnnotation, end: coords });
          break;
        case "circle":
          const dx = coords.x - draftAnnotation.center.x;
          const dy = coords.y - draftAnnotation.center.y;
          setDraftAnnotation({
            ...draftAnnotation,
            radius: Math.sqrt(dx * dx + dy * dy),
          });
          break;
        case "arrow":
          setDraftAnnotation({ ...draftAnnotation, end: coords });
          break;
        case "highlight":
          setDraftAnnotation({
            ...draftAnnotation,
            area: {
              x: draftAnnotation.area.x,
              y: draftAnnotation.area.y,
              width: coords.x - draftAnnotation.area.x,
              height: coords.y - draftAnnotation.area.y,
            },
          });
          break;
        default:
          break;
      }
    }
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    setIsDragging(false);

    if (draftAnnotation) {
      // Commit draft to annotations
      const committed = { ...draftAnnotation };
      setAnnotations((prev) => [...prev, committed]);
      setDraftAnnotation(null);
    }

    // Save to history for undo/redo
    setHistory((prev) => [
      ...prev.slice(0, historyIndex + 1),
      [...annotations],
    ]);
    setHistoryIndex((prev) => prev + 1);
  };

  const handleWheel = (event) => {
    event.preventDefault();
    const delta = event.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.1, Math.min(5, scale * delta));

    // Calculate new offset to zoom towards mouse position
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    setOffset((prev) => ({
      x: mouseX - (mouseX - prev.x) * (newScale / scale),
      y: mouseY - (mouseY - prev.y) * (newScale / scale),
    }));

    setScale(newScale);
  };

  const handleZoom = (factor) => {
    const newScale = Math.max(0.1, Math.min(5, scale * factor));
    setScale(newScale);
  };

  const handleZoomFit = () => {
    if (!image || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const scaleX = canvas.width / image.width;
    const scaleY = canvas.height / image.height;
    const newScale = Math.min(scaleX, scaleY, 1);

    setScale(newScale);
    setOffset({
      x: (canvas.width - image.width * newScale) / 2,
      y: (canvas.height - image.height * newScale) / 2,
    });
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setAnnotations(history[historyIndex - 1]);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setAnnotations(history[historyIndex + 1]);
    }
  };

  const handleSaveAnnotation = () => {
    if (!currentAnnotation.text.trim()) return;

    const newAnnotation = {
      ...currentAnnotation,
      timestamp: new Date().toISOString(),
    };

    setAnnotations((prev) => [...prev, newAnnotation]);
    setAnnotationDialog(false);
    setCurrentAnnotation({
      id: "",
      type: "comment",
      x: 0,
      y: 0,
      text: "",
      color: "#ff0000",
      priority: "normal",
      author: "Current User",
      timestamp: new Date().toISOString(),
    });
  };

  const handleSaveChanges = () => {
    if (onSave) {
      onSave({
        ...activePhoto,
        annotations: annotations,
        annotationData: {
          scale: realWorldScale,
          unit: measurementUnit,
          totalAnnotations: annotations.length,
          lastModified: new Date().toISOString(),
        },
      });
    }
  };

  const handleExport = (format = "png") => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const link = document.createElement("a");
    link.download = `annotated_${photo.filename || "photo"}.${format}`;
    link.href = canvas.toDataURL(`image/${format}`);
    link.click();
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header Toolbar */}
      <Paper
        elevation={2}
        sx={{ p: 1, display: "flex", alignItems: "center", gap: 1 }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Photo Annotation - {activePhoto?.filename}
        </Typography>

        {/* Optional photo selector - Always show for debugging */}
        {photoList && (
          <FormControl size="small" sx={{ minWidth: 220, mr: 1 }}>
            <InputLabel>Select Photo</InputLabel>
            <Select
              label="Select Photo"
              value={activePhoto?.id || activePhoto?.filename || ""}
              onChange={(e) => {
                const next = photoList.find(
                  (p) => (p.id || p.filename) === e.target.value
                );
                if (next) {
                  setAnnotations(next.annotations || []);
                  setHistory([]);
                  setHistoryIndex(-1);
                  setActivePhoto(next);
                }
              }}
            >
              {photoList.length > 0 ? (
                photoList.map((p, index) => {
                  return (
                    <MenuItem
                      key={p.id || p.filename || index}
                      value={p.id || p.filename || index}
                    >
                      {p.filename || p.name || p.id || `Photo ${index + 1}`}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem value="" disabled>
                  No photos available
                </MenuItem>
              )}
            </Select>
          </FormControl>
        )}

        <ButtonGroup size="small">
          <Button onClick={handleUndo} disabled={historyIndex <= 0}>
            <Undo />
          </Button>
          <Button
            onClick={handleRedo}
            disabled={historyIndex >= history.length - 1}
          >
            <Redo />
          </Button>
        </ButtonGroup>

        <Divider orientation="vertical" flexItem />

        <ButtonGroup size="small">
          <Button onClick={() => handleZoom(1.2)}>
            <ZoomIn />
          </Button>
          <Button onClick={() => handleZoom(0.8)}>
            <ZoomOut />
          </Button>
          <Button onClick={handleZoomFit}>
            <ZoomOutMap />
          </Button>
        </ButtonGroup>

        <Chip
          label={`Zoom ${Math.round(scale * 100)}%`}
          size="small"
          sx={{ ml: 1 }}
        />

        <Divider orientation="vertical" flexItem />

        <Button
          startIcon={<Save />}
          onClick={handleSaveChanges}
          disabled={readOnly}
        >
          Save
        </Button>
        <Button startIcon={<Download />} onClick={() => handleExport("png")}>
          Export
        </Button>
        <Button onClick={onCancel}>Close</Button>
      </Paper>

      <Box sx={{ display: "flex", flex: 1 }}>
        {/* Tools Panel */}
        {toolsPanelOpen && !readOnly && (
          <Paper elevation={2} sx={{ width: 280, p: 2, overflow: "auto" }}>
            <Typography variant="h6" gutterBottom>
              Tools
            </Typography>

            {/* Drawing Tools */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Drawing Tools
              </Typography>
              <ToggleButtonGroup
                value={currentTool}
                exclusive
                onChange={(e, value) => value && setCurrentTool(value)}
                size="small"
                sx={{ flexWrap: "wrap" }}
              >
                {drawingTools.map((tool) => (
                  <ToggleButton key={tool.id} value={tool.id} sx={{ m: 0.5 }}>
                    <Tooltip title={tool.label}>{tool.icon}</Tooltip>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* Color Palette */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Color
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {colorPalette.map((color) => (
                  <Box
                    key={color}
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: color,
                      border:
                        drawingColor === color
                          ? "2px solid #000"
                          : "1px solid #ccc",
                      borderRadius: 1,
                      cursor: "pointer",
                      "&:hover": { opacity: 0.8 },
                    }}
                    onClick={() => setDrawingColor(color)}
                  />
                ))}
              </Box>
            </Box>

            {/* Tool Settings */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Settings
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Line Width: {lineWidth}px
                </Typography>
                <Slider
                  value={lineWidth}
                  onChange={(e, value) => setLineWidth(value)}
                  min={1}
                  max={20}
                  step={1}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">Font Size: {fontSize}px</Typography>
                <Slider
                  value={fontSize}
                  onChange={(e, value) => setFontSize(value)}
                  min={10}
                  max={48}
                  step={2}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Fill Opacity: {Math.round(fillOpacity * 100)}%
                </Typography>
                <Slider
                  value={fillOpacity}
                  onChange={(e, value) => setFillOpacity(value)}
                  min={0}
                  max={1}
                  step={0.05}
                  size="small"
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={showGrid}
                    onChange={(e) => setShowGrid(e.target.checked)}
                  />
                }
                label="Show Grid"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={snapToGrid}
                    onChange={(e) => setSnapToGrid(e.target.checked)}
                  />
                }
                label="Snap to Grid"
              />
            </Box>

            {/* Measurement Tools */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Measurements
              </Typography>

              <TextField
                fullWidth
                size="small"
                label="Known Distance"
                value={knownDistance}
                onChange={(e) => setKnownDistance(e.target.value)}
                sx={{ mb: 1 }}
                placeholder="Enter known distance for calibration"
              />

              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  value={measurementUnit}
                  label="Unit"
                  onChange={(e) => setMeasurementUnit(e.target.value)}
                >
                  <MenuItem value="inches">Inches</MenuItem>
                  <MenuItem value="feet">Feet</MenuItem>
                  <MenuItem value="cm">Centimeters</MenuItem>
                  <MenuItem value="meters">Meters</MenuItem>
                </Select>
              </FormControl>

              <Button
                fullWidth
                size="small"
                variant="outlined"
                onClick={() => setCalibrationMode(!calibrationMode)}
              >
                {calibrationMode ? "Exit Calibration" : "Calibrate Scale"}
              </Button>

              {showGrid && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Grid Size: {gridSize}px
                  </Typography>
                  <Slider
                    value={gridSize}
                    onChange={(e, value) => setGridSize(value)}
                    min={5}
                    max={200}
                    step={5}
                    size="small"
                  />
                </Box>
              )}

              {measurementPoints.length > 0 && (
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  onClick={() => setMeasurementPoints([])}
                  sx={{ mt: 1 }}
                >
                  Clear Measurements
                </Button>
              )}
            </Box>
          </Paper>
        )}

        {/* Main Canvas Area */}
        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            position: "relative",
            overflow: "hidden",
            cursor:
              currentTool === "pen"
                ? "crosshair"
                : currentTool === "text"
                  ? "text"
                  : "default",
          }}
        >
          <canvas
            ref={canvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onWheel={handleWheel}
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              background: "#f5f5f5",
            }}
          />

          {/* Removed bottom-left zoom chip to avoid overlapping/scrollbars. Now shown in header */}

          {/* Annotation counter */}
          <Paper
            elevation={2}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              p: 1,
            }}
          >
            <Typography variant="caption">
              Annotations: {annotations.length}
            </Typography>
          </Paper>
        </Box>

        {/* Layers Panel */}
        {layersPanelOpen && (
          <Paper elevation={2} sx={{ width: 300, p: 2, overflow: "auto" }}>
            <Typography variant="h6" gutterBottom>
              Annotations
            </Typography>

            <List dense>
              {annotations.map((annotation, index) => {
                const annotationType = annotationTypes.find(
                  (t) => t.value === annotation.type
                );
                return (
                  <ListItem key={annotation.id}>
                    <ListItemIcon>
                      {annotationType?.icon || <Comment />}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        annotation.text || `${annotation.type} ${index + 1}`
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(annotation.timestamp).toLocaleString()}
                          </Typography>
                          {annotation.author && (
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              By: {annotation.author}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setAnnotations((prev) =>
                            prev.filter((a) => a.id !== annotation.id)
                          );
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>

            {annotations.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 2 }}
              >
                No annotations yet. Start drawing or adding comments.
              </Typography>
            )}
          </Paper>
        )}
      </Box>

      {/* Floating Action Buttons */}
      <Box sx={{ position: "fixed", bottom: 20, right: 20 }}>
        <SpeedDial
          ariaLabel="Annotation tools"
          sx={{ position: "absolute", bottom: 16, right: 16 }}
          icon={<SpeedDialIcon />}
          direction="up"
        >
          <SpeedDialAction
            icon={<Layers />}
            tooltipTitle="Toggle Layers"
            onClick={() => setLayersPanelOpen(!layersPanelOpen)}
          />
          <SpeedDialAction
            icon={<Tune />}
            tooltipTitle="Toggle Tools"
            onClick={() => setToolsPanelOpen(!toolsPanelOpen)}
          />
          <SpeedDialAction
            icon={<LayersClear />}
            tooltipTitle="Clear All"
            onClick={() => {
              if (window.confirm("Clear all annotations?")) {
                setAnnotations([]);
                setMeasurementPoints([]);
              }
            }}
          />
        </SpeedDial>
      </Box>

      {/* Annotation Dialog */}
      <Dialog
        open={annotationDialog}
        onClose={() => setAnnotationDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Annotation</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Annotation Type</InputLabel>
              <Select
                value={currentAnnotation.type}
                label="Annotation Type"
                onChange={(e) =>
                  setCurrentAnnotation({
                    ...currentAnnotation,
                    type: e.target.value,
                  })
                }
              >
                {annotationTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {type.icon}
                      {type.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Comment"
              value={currentAnnotation.text}
              onChange={(e) =>
                setCurrentAnnotation({
                  ...currentAnnotation,
                  text: e.target.value,
                })
              }
              placeholder="Enter your comment or note..."
              sx={{ mb: 2 }}
            />

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={currentAnnotation.priority}
                label="Priority"
                onChange={(e) =>
                  setCurrentAnnotation({
                    ...currentAnnotation,
                    priority: e.target.value,
                  })
                }
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnnotationDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveAnnotation}
            disabled={!currentAnnotation.text.trim()}
          >
            Add Annotation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PpPhotoAnnotationTools;
